import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import {
  EjendomSted,
  ProduktionsEnhedAdresse
} from 'src/app/api/models';
import { EjendommeService, VirksomhederService } from 'src/app/api/services';
import { FormHelper } from '../../helpers/form-helper';
import { NotificationService } from '../../notifications/notification.service';
import { TekstResourcePipe } from '../../pipes/tekstresource.pipe';

@Component({
  selector: 'bom-virksomhedsoegning',
  templateUrl: './virksomhedsoegning.component.html',
  styleUrls: ['./virksomhedsoegning.component.scss'],
})
export class VirksomhedsoegningComponent implements OnInit, OnDestroy {
  @Output()
  searchButtonToggle = new EventEmitter<boolean>();
  @Output()
  searchVirksomhed = new EventEmitter<EjendomSted>();
  @Output()
  resetSted = new EventEmitter<string>();
  @Output()
  valgtStedLoadingToggle = new EventEmitter<boolean>();
  @Input()
  searchClicked: Observable<any>;

  notificationsService: NotificationService;
  private _pEnhedOptions: ProduktionsEnhedAdresse[] = [];
  searching = false;
  isPnummerErroneous = false;
  searchSubscription: Subscription;
  form: FormGroup;

  set pEnhedOptions(pEnhedOptions: ProduktionsEnhedAdresse[]) {
    this._pEnhedOptions = [{ pNummer: 'Vælg P-nummer', fuldAdresse: '' }, ...pEnhedOptions];
  }

  get pEnhedOptions() {
    return this._pEnhedOptions;
  }

  get pNummerValidator() {
    return this.form.get('pnummerValidator');
  }

  get cvrValidator() {
    return this.form.get('cvrValidator');
  }

  constructor(
    private virksomhederService: VirksomhederService,
    private notificationService: NotificationService,
    private tekstResourcePipe: TekstResourcePipe,
    private ejendommeService: EjendommeService
  ) {
    this.form = new FormGroup({
      cvrValidator: FormHelper.OnlyDigitsWithspaces,
      pnummerValidator: FormHelper.NoValidators,
    });
    this.resetModel();
  }

  ngOnInit(): void {
    this.searchSubscription = this.searchClicked.subscribe(() => {
      this.search();
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  /**
   * Invalidates/resets the model when input changes
   */
  inputChange(input: string) {
    input && !this.cvrValidator.errors
      ? this.searchButtonToggle.emit(true)
      : this.searchButtonToggle.emit(false);
    this.resetModel();
  }

  resetModel() {
    this.pEnhedOptions = [];
    this.pNummerValidator.setValue(this.pEnhedOptions[0]);
    this.pNummerValidator.disable();
  }

  search() {
    FormHelper.RemoveWhitespace(this.cvrValidator);
    this.resetSted.emit(null);
    // check for cvr field length and errors
    if (
      this.cvrValidator.value.length !== FormHelper.CvrLength &&
      this.cvrValidator.value.length !== FormHelper.PNummerLength
    ) {
      this.setValidatorError(this.cvrValidator);
    } else if (
      this.cvrValidator.value.length === FormHelper.CvrLength &&
      !this.cvrValidator.errors
    ) {
      if (
        this.pNummerValidator.enabled &&
        !FormHelper.isPNummerValid(this.pNummerValidator.value.pNummer)
      ) {
        this.setValidatorError(this.pNummerValidator);
      } else if (
        this.pEnhedOptions.length > 2 &&
        FormHelper.isPNummerValid(this.pNummerValidator.value.pNummer) &&
        this.pNummerValidator.enabled
      ) {
        if (!!this.pNummerValidator.value.adresseId) {
          this.searchStedByAdresseId(this.pNummerValidator.value.adresseId);
        } else {
          this.resetSted.emit(
            this.tekstResourcePipe.transform(
              'Fejl.Notifikation.Virksomhedsoegning.IngenDARAdressePaaPNummer',
              {
                adresse: this.pNummerValidator.value.fuldAdresse,
              }
            )
          );
        }
      } else {
        this.searching = true;
        this.virksomhederService.getCvrInfo(this.cvrValidator.value).subscribe(response => {
          if (!!response) {
            this.handleFindPnummerResponse(response.produktionsEnheder);
          } else {
            this.searching = false;
            this.resetSted.emit(
              this.tekstResourcePipe.transform('Fejl.Notifikation.GeoService.IngenCVR')
            );
          }
        });
      }
    } else if (
      this.cvrValidator.value.length === FormHelper.PNummerLength &&
      !this.cvrValidator.errors
    ) {
      // Perform Pnummer search directly
      this.searchStedByPnummer(this.cvrValidator.value);
    }
  }

  pnummerChange() {
    if (this.pNummerValidator.value.adresse) {
      this.search();
    }
  }

  searchStedByAdresseId(adresseId: string) {
    this.valgtStedLoadingToggle.emit(true);
    this.ejendommeService.getEjendomsStedByAdresseId(adresseId).subscribe(sted => {
      // we display the sted for the p-nummer if it exists. If not it may be either because the p-nummer has an
      // "invalid" non-existing address or because the p-nummer itself does not exist
      if (!sted.externalCallSuccess) {
        this.notificationService.warn(
          this.tekstResourcePipe.transform('Fejl.Notifikation.EksternSystem.Error'),
          null,
          sted.correlationId
        );
      } else if (sted.ejendom) {
        this.searchVirksomhed.emit(sted.ejendom);
      } else {
        const pEnhed = this.pEnhedOptions.find(e => e.adresseId === adresseId);
        if (pEnhed) {
          const adresse = pEnhed.fuldAdresse;
          const message = this.tekstResourcePipe.transform('CVRIkkeGyldigAdresse', {
            adresse: adresse,
          });
          this.resetSted.emit(message);
        } else {
          this.resetSted.emit(
            this.tekstResourcePipe.transform(
              'Fejl.Notifikation.Virksomhedsoegning.IngenDataPaaPNummer',
              {
                error: 'P-nummer findes ikke.',
              }
            )
          );
        }
      }
    });
  }

  searchStedByPnummer(pNummer: string) {
    this.valgtStedLoadingToggle.emit(true);
    this.virksomhederService.findStedByPNummer(pNummer).subscribe(sted => {
      // we display the sted for the p-nummer if it exists. If not it may be either because the p-nummer has an
      // "invalid" non-existing address or because the p-nummer itself does not exist
      if (!sted.externalCallSuccess) {
        this.notificationService.warn(
          this.tekstResourcePipe.transform('Fejl.Notifikation.EksternSystem.Error'),
          null,
          sted.correlationId
        );
      } else if (!!sted.ejendom?.adgangAdresse?.adresseId) {
        this.searchVirksomhed.emit(sted.ejendom);
      } else {
        const pEnhed = this.pEnhedOptions.find(e => e.pNummer === pNummer);
        if (pEnhed) {
          const adresse = pEnhed.fuldAdresse;
          const message = this.tekstResourcePipe.transform('CVRIkkeGyldigAdresse', {
            adresse: adresse,
          });
          this.resetSted.emit(message);
        } else if (!!sted.ejendom) {
          this.resetSted.emit(
            this.tekstResourcePipe.transform(
              'Fejl.Notifikation.Virksomhedsoegning.IngenDARAdressePaaPNummer',
              {
                adresse: sted.ejendom.adgangAdresse.adresseBetegnelse,
              }
            )
          );
        } else {
          this.resetSted.emit(
            this.tekstResourcePipe.transform(
              'Fejl.Notifikation.Virksomhedsoegning.IngenDataPaaPNummer',
              {
                error: 'P-nummer findes ikke.',
              }
            )
          );
        }
      }
    });
  }

  onBlur() {
    const value = this.cvrValidator.value.replace(/ +/g, '');
    if (value.length !== FormHelper.CvrLength && value.length !== FormHelper.PNummerLength) {
      FormHelper.RemoveWhitespace(this.cvrValidator);
      this.setValidatorError(this.cvrValidator);
    }
    !this.cvrValidator.errors
      ? this.searchButtonToggle.emit(true)
      : this.searchButtonToggle.emit(false);
  }

  private handleFindPnummerResponse(response: ProduktionsEnhedAdresse[]) {
    this.searching = false;
    this.pEnhedOptions = response.sort((a, b) => (a?.pNummer > b?.pNummer ? 1 : -1));
    this.pNummerValidator.enable();
    this.pNummerValidator.setValue(this.pEnhedOptions[0]);
    // we always have default "Vælg P-nummer". Therefor arraylength of 1 means no searchresult
    if (this.pEnhedOptions.length === 1) {
      // If CVR has no pnumre, show no results feedback to user
      this.resetSted.emit(
        this.tekstResourcePipe.transform('Fejl.Notifikation.GeoService.IngenPNummer.Description')
      );
      this.resetModel();
    } else if (this.pEnhedOptions.length === 2 && !!this.pEnhedOptions[1]?.adresseId) {
      // If there is only 1 pnummer, just search for this
      this.pNummerValidator.setValue(this.pEnhedOptions[1]);
      this.pNummerValidator.disable();
      this.searchStedByAdresseId(this.pNummerValidator.value.adresseId);
    }
  }

  private setValidatorError(control: AbstractControl) {
    control.setErrors({ error: true });
  }
}
